import React, { useState } from "react";
import {
  Box,
  Button,
  Divider,
  IconButton,
  TextField,
  Typography,
} from "@material-ui/core";
import DeleteIcon from "@material-ui/icons/Delete";
import EditIcon from "@material-ui/icons/Edit";
import { withStyles } from "@material-ui/styles";
import moment from "moment";
import { DATE_TIME_FORMATS } from "../../../../constants/Constants";

const styles = (theme) => ({
  root: {
    marginTop: theme.spacing(1),
  },
  title: {
    fontSize: 12,
    fontWeight: 600,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.5),
    marginTop: theme.spacing(1),
  },
  commentRow: {
    marginBottom: theme.spacing(0.5),
    padding: theme.spacing(0.75, 1),
    borderRadius: 4,
    backgroundColor: "#f5f5f5",
  },
  meta: {
    color: "#9e9e9e",
    fontSize: 10,
    lineHeight: 1.4,
  },
  commentText: {
    color: theme.palette.text.secondary,
    fontSize: 12,
    lineHeight: 1.4,
    marginTop: 2,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  addActions: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(0.5),
    gap: theme.spacing(1),
  },
  addCommentContainer: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(1),
    width: "100%",
  },
  actionIcon: {
    padding: 2,
    "& svg": {
      fontSize: 16,
    },
  },
  addCommentButton: {
    textTransform: "none",
    fontWeight: 500,
    fontSize: 11,
  },
  saveButton: {
    textTransform: "none",
    fontSize: 11,
  },
  updateButton: {
    textTransform: "none",
    fontSize: 11,
  },
  cancelButton: {
    textTransform: "none",
    fontSize: 11,
    color: "#212121 !important",
    borderColor: "#212121 !important",
  },
});

const getCommentAuthor = (comment) => {
  const firstName = (comment?.userFirstName || "").trim();
  const lastName = (comment?.userLastName || "").trim();
  const fullName = `${firstName} ${lastName}`.trim();
  return fullName || comment?.lastUpdatedBy || comment?.createdBy || "Unknown user";
};

const getCommentTime = (comment) => {
  const timestamp = comment?.lastUpdatedDate ?? comment?.createdDate;
  if (!timestamp) return "";
  const ms = Number(timestamp);
  if (!Number.isNaN(ms)) {
    return moment(ms).format(DATE_TIME_FORMATS.DD_MMM_YYYY_H_MM_SS_A);
  }
  return "";
};

export const QuestionCommentsPanel = ({ classes, element, canAddComments, onAdd, onEdit, onDelete }) => {
  const [newComment, setNewComment] = useState("");
  const [editingCommentId, setEditingCommentId] = useState("");
  const [editingCommentText, setEditingCommentText] = useState("");
  const [showAddBox, setShowAddBox] = useState(false);

  const comments = Array.isArray(element?.notes) ? element.notes : [];

  const handleAddComment = () => {
    if (!newComment.trim()) return;
    onAdd(newComment);
    setNewComment("");
    setShowAddBox(false);
  };

  const handleEditStart = (comment, commentIndex) => {
    setEditingCommentId(commentIndex);
    setEditingCommentText(comment.comment || "");
  };

  const handleEditSave = () => {
    if (!editingCommentText.trim()) return;
    onEdit(editingCommentId, editingCommentText);
    setEditingCommentId("");
    setEditingCommentText("");
  };

  const handleDeleteComment = (commentIndex) => {
    onDelete(commentIndex);
  };

  return (
    <Box className={classes.root}>
      {comments.length > 0 && (
        <>
          <Divider />
          <Typography variant="subtitle2" className={classes.title}>
            Reviewer Comments
          </Typography>
        </>
      )}

      {comments.map((comment, commentIndex) => (
        <Box
          key={`${comment?.lastUpdatedBy || "unknown"}-${commentIndex}`}
          className={classes.commentRow}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography className={classes.meta}>
              {`${getCommentAuthor(comment)}${
                getCommentTime(comment) ? ` | ${getCommentTime(comment)}` : ""
              }`}
            </Typography>
            {canAddComments ? (
              <Box>
                <IconButton
                  size="small"
                  className={classes.actionIcon}
                  onClick={() => handleEditStart(comment, commentIndex)}
                  aria-label="edit-comment"
                >
                  <EditIcon fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  className={classes.actionIcon}
                  onClick={() => handleDeleteComment(commentIndex)}
                  aria-label="delete-comment"
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            ) : null}
          </Box>

          {editingCommentId === commentIndex && canAddComments ? (
            <Box>
              <TextField
                value={editingCommentText}
                onChange={(event) => setEditingCommentText(event.target.value)}
                fullWidth
                multiline
                rows={2}
                variant="outlined"
                size="small"
              />
              <Box className={classes.addActions}>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.cancelButton}
                  onClick={() => {
                    setEditingCommentId("");
                    setEditingCommentText("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  className={classes.updateButton}
                  onClick={handleEditSave}
                >
                  Update
                </Button>
              </Box>
            </Box>
          ) : (
            <Typography className={classes.commentText}>{comment.comment}</Typography>
          )}
        </Box>
      ))}

      {canAddComments && !showAddBox ? (
        <Box className={classes.addCommentContainer}>
          <Button
            size="small"
            variant="outlined"
            color="primary"
            className={classes.addCommentButton}
            onClick={() => setShowAddBox(true)}
          >
            + Add Comment
          </Button>
        </Box>
      ) : null}

      {canAddComments && showAddBox ? (
        <Box>
          <TextField
            value={newComment}
            onChange={(event) => setNewComment(event.target.value)}
            fullWidth
            multiline
            rows={2}
            variant="outlined"
            size="small"
            placeholder="Add a reviewer comment"
          />
          <Box className={classes.addActions}>
            <Button
              size="small"
              variant="outlined"
              className={classes.cancelButton}
              onClick={() => {
                setShowAddBox(false);
                setNewComment("");
              }}
            >
              Cancel
            </Button>
            <Button
              size="small"
              variant="contained"
              className={classes.saveButton}
              disabled={!newComment.trim()}
              onClick={handleAddComment}
            >
              Save
            </Button>
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default withStyles(styles)(QuestionCommentsPanel);
